import { Flex, Input, Label, Text, Textarea, ThemeUIStyleObject } from 'theme-ui';
import { forwardRef, MouseEvent, RefObject } from 'react';

type Props = {
  name: string;
  theme: 'dark' | 'light';
  placeholder: string;
  forwardSx?: ThemeUIStyleObject;
  required?: boolean;
  error?: string | null;
  type?: string;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  min?: string | number;
  max?: string | number;
};

const InputWithPlaceholder = forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(
  ({ name, theme, placeholder, forwardSx = {}, required, error, type, onClick, min, max, ...props }, ref) => {
    const id = name + '-input';
    const styles: ThemeUIStyleObject = {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderRadius: 0,
      px: 0,
      py: type === 'textarea' ? '0' : '1.3rem',
      resize: type === 'textarea' && 'none',
      '&::placeholder': {
        opacity: 0,
        visibility: 'hidden',
      },
      '&:not(:placeholder-shown) + label': {
        opacity: 0,
        visibility: 'hidden',
      },
      fontSize: 'inherit',
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
        WebkitBoxShadow: `0 0 0 30px ${theme === 'dark' ? '#333' : '#e9e9e9'} inset !important`,
      },
      '&:-webkit-autofill': {
        WebkitTextFillColor: `${theme === 'dark' ? 'white' : '#333'} !important`,
      },
      ...forwardSx,
    };

    return (
      <Flex
        sx={{
          backgroundColor: theme === 'dark' ? 'darkGrey' : 'cardGrey',
          borderRadius: type === 'textarea' ? '2rem' : '10rem',
          alignItems: 'center',
          fontSize: '1.4rem',
          height: type === 'textarea' ? '10rem' : '4rem',
          py: '1.3rem',
          pl: '1.6rem',
          position: 'relative',
          pr: '.6rem',
        }}
      >
        {type === 'textarea' ? (
          <Textarea
            required={required}
            ref={ref as RefObject<HTMLTextAreaElement>}
            id={id}
            name={name}
            sx={styles}
            variant="text.formInput"
            placeholder={placeholder}
            {...props}
          />
        ) : (
          <Input
            required={required}
            ref={ref as RefObject<HTMLInputElement>}
            id={id}
            name={name}
            type={type}
            sx={styles}
            variant="text.formInput"
            placeholder={placeholder}
            onClick={(e) => onClick && onClick(e)}
            min={min}
            max={max}
            {...props}
          />
        )}
        <Label
          htmlFor={id}
          sx={{
            position: 'absolute',
            left: '1.6rem',
            top: type === 'textarea' && '1.3rem',
            width: 'fit-content',
            fontSize: '1.4rem',
            transition: 'opacity 0.3s ease-in-out',
            color: theme === 'dark' ? 'UIGray1' : 'darkGrey',
            pointerEvents: 'none',
          }}
        >
          {placeholder}
        </Label>
        {error ? (
          <Text
            sx={{
              position: 'absolute',
              color: 'error',
              top: '100%',
              fontSize: '1rem',
              lineHeight: 1.5,
            }}
          >
            {error}
          </Text>
        ) : null}
      </Flex>
    );
  }
);

export default InputWithPlaceholder;
